import { useEffect, useMemo, useState } from "react";
import { supabase } from "../lib/supabase";

function getAccountLabel(account) {
  return account?.display_name || account?.business_name || account?.account_name || account?.account_id || "Unknown account";
}

function formatDate(value) {
  if (!value) return "—";

  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function getCompletionWindows() {
  const now = new Date();
  const last30 = new Date(now);
  last30.setDate(now.getDate() - 30);

  const ytdStart = new Date(now.getFullYear(), 0, 1);
  const lastYearStart = new Date(now.getFullYear() - 1, 0, 1);
  const lastYearEnd = new Date(now.getFullYear(), 0, 1);

  return {
    last30Start: last30.toISOString(),
    ytdStart: ytdStart.toISOString(),
    lastYearStart: lastYearStart.toISOString(),
    lastYearEnd: lastYearEnd.toISOString(),
    currentYear: now.getFullYear(),
    lastYear: now.getFullYear() - 1,
  };
}

function getSourceRecommendationId(brief) {
  return brief?.source_context?.recommendation_id || brief?.content_brief?.source_recommendation?.id || null;
}

function briefToClipboardText(brief) {
  const contentBrief = brief?.content_brief || {};
  const structure = Array.isArray(brief?.suggested_structure) ? brief.suggested_structure : [];
  const guardrails = Array.isArray(brief?.guardrails) ? brief.guardrails : [];

  return [
    brief?.title || "Agent Smith Action Brief",
    "",
    "Product area:",
    getProductAreaLabel(brief?.product_area),
    "",
    "Recommended action:",
    brief?.recommended_action || "—",
    "",
    "Why this matters:",
    brief?.why_this_matters || "—",
    "",
    "AI writing prompt:",
    brief?.ai_writing_prompt || "—",
    "",
    "Suggested structure:",
    ...structure.map((item) => `- ${item}`),
    "",
    "Guardrails:",
    ...guardrails.map((item) => `- ${item}`),
    "",
    "Content brief:",
    JSON.stringify(contentBrief, null, 2),
  ].join("\n");
}

function formatEventType(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const AGENT_SMITH_PRODUCT_AREAS = [
  {
    value: "ai_presence_builder",
    label: "AI Presence Builder",
    description: "Entity clarity, proof, governance, and structured content outputs.",
    tone: "blue",
  },
  {
    value: "competitive",
    label: "Competitive",
    description: "Comparison, positioning, shortlist, and competitive proof outputs.",
    tone: "violet",
  },
  {
    value: "local",
    label: "Local",
    description: "Local entity, service-area, trust, review, and proof outputs.",
    tone: "green",
  },
];

const PRODUCT_AREA_VALUES = AGENT_SMITH_PRODUCT_AREAS.map((area) => area.value);

function getProductAreaLabel(value) {
  const match = AGENT_SMITH_PRODUCT_AREAS.find((area) => area.value === value);
  return match?.label || "Other";
}

function getProductAreaDescription(value) {
  const match = AGENT_SMITH_PRODUCT_AREAS.find((area) => area.value === value);
  return match?.description || "Action Brief output.";
}

function getProductAreaPillStyle(value) {
  const match = AGENT_SMITH_PRODUCT_AREAS.find((area) => area.value === value);

  if (match?.tone === "green") {
    return {
      background: "#dcfce7",
      borderColor: "#86efac",
      color: "#166534",
    };
  }

  if (match?.tone === "violet") {
    return {
      background: "#ede9fe",
      borderColor: "#c4b5fd",
      color: "#5b21b6",
    };
  }

  return {
    background: "#dbeafe",
    borderColor: "#bfdbfe",
    color: "#1d4ed8",
  };
}

const ACTION_BRIEF_FILTERS = [
  {
    value: "comparison",
    label: "Comparison",
    description: "Work that addresses competitor or comparison visibility gaps.",
    style: { background: "#f0f9ff", borderColor: "#bae6fd", color: "#0369a1" },
  },
  {
    value: "entity",
    label: "Entity",
    description: "Work that improves entity clarity, structure, or governance.",
    style: { background: "#f5f3ff", borderColor: "#ddd6fe", color: "#5b21b6" },
  },
  {
    value: "review",
    label: "Review",
    description: "Work that needs operator review, classification, or validation.",
    style: { background: "#fff7ed", borderColor: "#fed7aa", color: "#9a3412" },
  },
  {
    value: "content",
    label: "Content",
    description: "Work that improves content, coverage, pages, FAQs, or proof.",
    style: { background: "#f0fdf4", borderColor: "#bbf7d0", color: "#166534" },
  },
  {
    value: "cleanup",
    label: "Cleanup",
    description: "Work that suppresses, corrects, or cleans up weak signals.",
    style: { background: "#fff1f2", borderColor: "#fecaca", color: "#991b1b" },
  },
];

function getActionBriefFilterKey(brief) {
  const combined = [
    brief?.action_type,
    brief?.title,
    brief?.recommended_action,
    brief?.why_this_matters,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (combined.includes("comparison") || combined.includes("competitor")) return "comparison";
  if (combined.includes("entity") || combined.includes("schema") || combined.includes("governance")) return "entity";
  if (combined.includes("review") || combined.includes("classif") || combined.includes("validate")) return "review";
  if (combined.includes("cleanup") || combined.includes("suppress") || combined.includes("false positive")) return "cleanup";
  if (combined.includes("content") || combined.includes("page") || combined.includes("faq") || combined.includes("proof")) return "content";
  return null;
}

function getActionBriefFilter(value) {
  return ACTION_BRIEF_FILTERS.find((filter) => filter.value === value) || null;
}

const emptyCompletionMetrics = {
  currentRun: 0,
  last30: 0,
  ytd: 0,
  lastYear: 0,
  allTime: 0,
  latestRunId: null,
  currentYear: new Date().getFullYear(),
  lastYearLabel: new Date().getFullYear() - 1,
};

const cardStyle = {
  border: "1px solid #e2e8f0",
  background: "white",
  borderRadius: 16,
  padding: 18,
  boxShadow: "0 12px 26px rgba(15, 23, 42, 0.05)",
};

const buttonStyle = {
  border: "1px solid #4f46e5",
  background: "#4f46e5",
  color: "white",
  borderRadius: 999,
  padding: "9px 14px",
  fontSize: 13,
  fontWeight: 900,
  cursor: "pointer",
};

function AgentSmithActionBriefs({ session }) {
  const [isCheckingAdmin, setIsCheckingAdmin] = useState(true);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [recentRuns, setRecentRuns] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [briefs, setBriefs] = useState([]);
  const [actionEvents, setActionEvents] = useState([]);
  const [completionMetrics, setCompletionMetrics] = useState(emptyCompletionMetrics);
  const [status, setStatus] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [updatingBriefId, setUpdatingBriefId] = useState(null);
  const [productAreaFilter, setProductAreaFilter] = useState("all");
  const [actionBriefFilter, setActionBriefFilter] = useState("all");
  const [completedSectionOpen, setCompletedSectionOpen] = useState(false);

  const selectedAccount = useMemo(
    () => accounts.find((account) => account.account_id === selectedAccountId) || null,
    [accounts, selectedAccountId]
  );

  const latestCompletedRun = useMemo(
    () => recentRuns.find((run) => run.status === "completed") || recentRuns[0] || null,
    [recentRuns]
  );

  const scopedBriefs = useMemo(() => {
    return briefs.filter((brief) => {
      if (productAreaFilter !== "all" && brief.product_area !== productAreaFilter) return false;
      if (actionBriefFilter === "all") return true;
      if (actionBriefFilter === "open") return !brief.marked_complete_at;
      if (actionBriefFilter === "complete") return Boolean(brief.marked_complete_at);
      return getActionBriefFilterKey(brief) === actionBriefFilter;
    });
  }, [briefs, productAreaFilter, actionBriefFilter]);

  const openBriefs = useMemo(
    () => scopedBriefs.filter((brief) => !brief.marked_complete_at),
    [scopedBriefs]
  );

  const completedBriefs = useMemo(
    () => scopedBriefs.filter((brief) => Boolean(brief.marked_complete_at)),
    [scopedBriefs]
  );

  const productAreaCounts = useMemo(() => {
    return AGENT_SMITH_PRODUCT_AREAS.map((area) => {
      const areaBriefs = briefs.filter((brief) => brief.product_area === area.value);
      return {
        ...area,
        total: areaBriefs.length,
        open: areaBriefs.filter((brief) => !brief.marked_complete_at).length,
        complete: areaBriefs.filter((brief) => Boolean(brief.marked_complete_at)).length,
      };
    });
  }, [briefs]);

  useEffect(() => {
    let isMounted = true;

    async function checkAdminAccess() {
      if (!session?.user?.id) {
        if (isMounted) {
          setHasAdminAccess(false);
          setIsCheckingAdmin(false);
        }
        return;
      }

      const { data, error } = await supabase
        .from("account_users")
        .select("id, role, is_active")
        .eq("user_id", session.user.id)
        .eq("is_active", true)
        .in("role", ["super_admin", "superadmin", "admin"])
        .limit(1);

      if (error) {
        console.error("Failed to check admin access:", error);
      }

      if (isMounted) {
        setHasAdminAccess(Boolean(data && data.length > 0));
        setIsCheckingAdmin(false);
      }
    }

    checkAdminAccess();

    return () => {
      isMounted = false;
    };
  }, [session?.user?.id]);

  useEffect(() => {
    if (!hasAdminAccess) return;
    loadAccounts();
  }, [hasAdminAccess]);

  useEffect(() => {
    if (!selectedAccountId) {
      setRecentRuns([]);
      setRecommendations([]);
      setBriefs([]);
      setActionEvents([]);
      setCompletionMetrics(emptyCompletionMetrics);
      setProductAreaFilter("all");
      return;
    }

    refreshAccountData(selectedAccountId);
  }, [selectedAccountId]);

  async function loadAccounts() {
    setIsLoading(true);
    setStatus("Loading accounts...");

    try {
      const { data, error } = await supabase
        .from("accounts")
        .select("account_id, account_name, display_name, business_name, website_domain, status, account_type, category, category_type, created_at")
        .order("created_at", { ascending: false });

      if (error) throw error;

      setAccounts(data || []);
      setStatus(`Loaded ${data?.length || 0} accounts.`);
    } catch (error) {
      console.error("Failed to load accounts:", error);
      setStatus(error?.message || "Failed to load accounts.");
    } finally {
      setIsLoading(false);
    }
  }

  function getCompletedOpportunityCountQuery(accountId) {
    return supabase
      .from("opportunity_action_briefs")
      .select("id", { count: "exact", head: true })
      .eq("account_id", accountId)
      .not("marked_complete_at", "is", null);
  }

  async function countCompletedOpportunities({ accountId, gte = null, lt = null, runId = null }) {
    let query = getCompletedOpportunityCountQuery(accountId);

    if (gte) query = query.gte("marked_complete_at", gte);
    if (lt) query = query.lt("marked_complete_at", lt);
    if (runId) query = query.eq("run_id", runId);

    const { count, error } = await query;

    if (error) throw error;

    return count || 0;
  }

  async function loadCompletionMetrics(accountId, runs = []) {
    const windows = getCompletionWindows();
    const latestRun = runs.find((run) => run.status === "completed") || runs[0] || null;

    const [currentRun, last30, ytd, lastYear, allTime] = await Promise.all([
      latestRun?.run_id
        ? countCompletedOpportunities({ accountId, runId: latestRun.run_id })
        : Promise.resolve(0),
      countCompletedOpportunities({ accountId, gte: windows.last30Start }),
      countCompletedOpportunities({ accountId, gte: windows.ytdStart }),
      countCompletedOpportunities({ accountId, gte: windows.lastYearStart, lt: windows.lastYearEnd }),
      countCompletedOpportunities({ accountId }),
    ]);

    return {
      currentRun,
      last30,
      ytd,
      lastYear,
      allTime,
      latestRunId: latestRun?.run_id || null,
      currentYear: windows.currentYear,
      lastYearLabel: windows.lastYear,
    };
  }

  async function refreshAccountData(accountId = selectedAccountId) {
    if (!accountId) return;

    setIsLoading(true);
    setStatus("Refreshing Agent Smith operating console...");

    try {
      const [runsResult, recommendationsResult, briefsResult, eventsResult] = await Promise.all([
        supabase
          .from("runs")
          .select("run_id, status, run_label, prompt_count, created_at, completed_at")
          .eq("account_id", accountId)
          .order("created_at", { ascending: false })
          .limit(8),
        supabase
          .from("entity_builder_recommendations")
          .select("id, run_id, recommendation_type, entity_name, canonical_entity_key, title, summary, priority, status, created_at")
          .eq("account_id", accountId)
          .eq("status", "open")
          .order("created_at", { ascending: false })
          .limit(20),
        supabase
          .from("opportunity_action_briefs")
          .select("id, run_id, product_area, action_type, title, recommended_action, why_this_matters, ai_writing_prompt, content_brief, suggested_structure, guardrails, source_context, generation_status, marked_complete_at, completed_by, is_active, created_at, updated_at")
          .eq("account_id", accountId)
          .in("product_area", PRODUCT_AREA_VALUES)
          .eq("is_active", true)
          .order("updated_at", { ascending: false })
          .limit(60),
        supabase
          .from("opportunity_action_brief_events")
          .select("id, action_brief_id, event_type, event_metadata, created_at")
          .eq("account_id", accountId)
          .order("created_at", { ascending: false })
          .limit(20),
      ]);

      if (runsResult.error) throw runsResult.error;
      if (recommendationsResult.error) throw recommendationsResult.error;
      if (briefsResult.error) throw briefsResult.error;
      if (eventsResult.error) throw eventsResult.error;

      const runs = runsResult.data || [];
      const metrics = await loadCompletionMetrics(accountId, runs);

      setRecentRuns(runs);
      setRecommendations(recommendationsResult.data || []);
      setBriefs(briefsResult.data || []);
      setActionEvents(eventsResult.data || []);
      setCompletionMetrics(metrics);
      setStatus("Agent Smith operating console refreshed.");
    } catch (error) {
      console.error("Failed to refresh Agent Smith operating console:", error);
      setStatus(error?.message || "Failed to refresh Agent Smith operating console.");
    } finally {
      setIsLoading(false);
    }
  }

  async function logActionBriefEvent({ brief = null, eventType, metadata = {} }) {
    if (!selectedAccountId || !eventType) return;

    const { error } = await supabase
      .from("opportunity_action_brief_events")
      .insert({
        action_brief_id: brief?.id || null,
        account_id: selectedAccountId,
        user_id: session?.user?.id || null,
        event_type: eventType,
        event_metadata: {
          ...metadata,
          action_brief_title: brief?.title || null,
          action_type: brief?.action_type || null,
          product_area: brief?.product_area || null,
          source_recommendation_id: getSourceRecommendationId(brief),
          logged_from: "agent_smith_action_briefs_page",
        },
      });

    if (error) {
      console.warn("Failed to log Action Brief event:", error);
    }
  }

  async function syncSourceRecommendationStatus(brief, action) {
    const recommendationId = getSourceRecommendationId(brief);

    if (!recommendationId || !selectedAccountId) {
      return { synced: false, reason: "missing_source_recommendation_id" };
    }

    let patch = { updated_at: new Date().toISOString() };

    if (action === "complete") {
      patch = {
        ...patch,
        status: "completed",
        completed_at: new Date().toISOString(),
        dismissed_at: null
      };
    }

    if (action === "reopen") {
      patch = {
        ...patch,
        status: "open",
        completed_at: null,
        dismissed_at: null
      };
    }

    if (action === "archive") {
      patch = {
        ...patch,
        status: "dismissed",
        dismissed_at: new Date().toISOString()
      };
    }

    const { error } = await supabase
      .from("entity_builder_recommendations")
      .update(patch)
      .eq("id", recommendationId)
      .eq("account_id", selectedAccountId);

    if (error) {
      console.warn("Failed to sync source recommendation status:", error);
      return { synced: false, reason: error.message };
    }

    return { synced: true, recommendation_id: recommendationId };
  }

  async function handleGenerateActionBriefs() {
    if (!selectedAccountId) {
      alert("Select an account first.");
      return;
    }

    const runId = latestCompletedRun?.run_id || null;
    const confirmed = window.confirm(
      [
        "Generate Agent Smith Action Briefs?",
        "",
        `Account: ${getAccountLabel(selectedAccount)}`,
        `Open recommendations: ${recommendations.length}`,
        runId ? `Run ID: ${runId}` : "Run ID: all open recommendations",
        "",
        "This will create or update AI Presence Builder Action Brief rows from open Entity Builder recommendations.",
        "It will not start an analysis run.",
      ].join("\n")
    );

    if (!confirmed) return;

    setIsGenerating(true);
    setStatus("Generating Agent Smith Action Briefs...");

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const response = await fetch("/api/admin-onboarding-generate-entity-builder-action-briefs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${sessionData.session?.access_token || ""}`,
        },
        body: JSON.stringify({
          account_id: selectedAccountId,
          run_id: runId,
        }),
      });

      const json = await response.json().catch(() => null);

      if (!response.ok || !json?.ok) {
        throw new Error(json?.error || "Failed to generate Agent Smith Action Briefs.");
      }

      await logActionBriefEvent({
        eventType: "action_briefs_generated",
        metadata: {
          generated_count: json.generated_count || 0,
          recommendation_count: json.recommendation_count || 0,
          run_id: runId,
          product_area: "ai_presence_builder",
        },
      });

      setStatus(
        `Generated ${json.generated_count || 0} Action Briefs from ${json.recommendation_count || 0} recommendations.`
      );

      await refreshAccountData(selectedAccountId);
    } catch (error) {
      console.error("Failed to generate Agent Smith Action Briefs:", error);
      setStatus(error?.message || "Failed to generate Agent Smith Action Briefs.");
      alert(error?.message || "Failed to generate Agent Smith Action Briefs.");
    } finally {
      setIsGenerating(false);
    }
  }

  async function copyBrief(brief) {
    const text = briefToClipboardText(brief);

    try {
      await navigator.clipboard.writeText(text);
      await logActionBriefEvent({
        brief,
        eventType: "action_brief_copied",
      });
      setStatus("Action Brief copied.");
      await refreshAccountData(selectedAccountId);
    } catch (error) {
      console.error("Failed to copy brief:", error);
      setStatus(error?.message || "Failed to copy Action Brief.");
    }
  }

  async function updateBriefWorkflow(brief, action) {
    if (!brief?.id) return;

    const labels = {
      complete: "mark this Action Brief complete",
      reopen: "reopen this Action Brief",
      archive: "archive this Action Brief"
    };

    const eventTypes = {
      complete: "action_brief_marked_complete",
      reopen: "action_brief_reopened",
      archive: "action_brief_archived",
    };

    if (action === "archive" && !window.confirm(`Archive this Action Brief?\n\n${brief.title}`)) {
      return;
    }

    setUpdatingBriefId(brief.id);
    setStatus(`Updating Action Brief: ${labels[action] || action}...`);

    try {
      let patch = { updated_at: new Date().toISOString() };

      if (action === "complete") {
        patch = {
          ...patch,
          marked_complete_at: new Date().toISOString(),
          completed_by: session?.user?.id || null
        };
      }

      if (action === "reopen") {
        patch = {
          ...patch,
          marked_complete_at: null,
          completed_by: null
        };
      }

      if (action === "archive") {
        patch = {
          ...patch,
          is_active: false
        };
      }

      const { error } = await supabase
        .from("opportunity_action_briefs")
        .update(patch)
        .eq("id", brief.id)
        .eq("account_id", selectedAccountId);

      if (error) throw error;

      const recommendationSync = await syncSourceRecommendationStatus(brief, action);

      await logActionBriefEvent({
        brief,
        eventType: eventTypes[action] || "action_brief_updated",
        metadata: {
          workflow_action: action,
          previous_marked_complete_at: brief.marked_complete_at || null,
          source_recommendation_sync: recommendationSync
        },
      });

      setStatus(`Action Brief updated: ${labels[action] || action}.`);
      await refreshAccountData(selectedAccountId);
    } catch (error) {
      console.error("Failed to update Action Brief:", error);
      setStatus(error?.message || "Failed to update Action Brief.");
      alert(error?.message || "Failed to update Action Brief.");
    } finally {
      setUpdatingBriefId(null);
    }
  }

  function renderActionBriefCard(brief) {
    const isComplete = Boolean(brief.marked_complete_at);
    const isUpdating = updatingBriefId === brief.id;
    const sourceRecommendationId = getSourceRecommendationId(brief);
    const areaPillStyle = getProductAreaPillStyle(brief.product_area);
    const actionFilter = getActionBriefFilter(getActionBriefFilterKey(brief));

    return (
      <div key={brief.id} style={{ border: "1px solid #dbeafe", background: isComplete ? "#f0fdf4" : "#f8fbff", borderRadius: 14, padding: 14 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <div style={{ fontWeight: 950, color: "#0f172a" }}>{brief.title}</div>
              <button
                type="button"
                title={isComplete ? "Completed briefs are no longer active work. Click to show completed briefs." : "Open briefs still need review or execution. Click to show active briefs."}
                aria-pressed={actionBriefFilter === (isComplete ? "complete" : "open")}
                onClick={() => setActionBriefFilter(isComplete ? "complete" : "open")}
                style={{ border: "0", borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, background: isComplete ? "#dcfce7" : "#dbeafe", color: isComplete ? "#166534" : "#1d4ed8", cursor: "pointer" }}
              >
                {isComplete ? "Complete" : "Open"}
              </button>
              <span style={{ border: `1px solid ${areaPillStyle.borderColor}`, borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, ...areaPillStyle }}>
                {getProductAreaLabel(brief.product_area)}
              </span>
              {actionFilter ? (
                <button
                  type="button"
                  title={actionFilter.description}
                  aria-pressed={actionBriefFilter === actionFilter.value}
                  onClick={() => setActionBriefFilter(actionFilter.value)}
                  style={{ border: `1px solid ${actionFilter.style.borderColor}`, borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, ...actionFilter.style, cursor: "pointer" }}
                >
                  {actionFilter.label}
                </button>
              ) : null}
            </div>
            <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>
              {brief.action_type || "Action type not supplied"} · {brief.generation_status} · Updated {formatDate(brief.updated_at)}
            </div>
            <div style={{ color: "#64748b", fontSize: 11, marginTop: 4 }}>
              {getProductAreaDescription(brief.product_area)}
            </div>
            {sourceRecommendationId ? (
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 4 }}>Source recommendation linked</div>
            ) : null}
            {isComplete ? (
              <div style={{ color: "#166534", fontSize: 12, marginTop: 4, fontWeight: 800 }}>Completed {formatDate(brief.marked_complete_at)}</div>
            ) : null}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <button type="button" onClick={() => copyBrief(brief)} style={{ ...buttonStyle, background: "#eff6ff", color: "#1d4ed8", borderColor: "#bfdbfe" }}>Copy</button>
            {isComplete ? (
              <button type="button" disabled={isUpdating} onClick={() => updateBriefWorkflow(brief, "reopen")} style={{ ...buttonStyle, background: "white", color: "#334155", borderColor: "#cbd5e1", opacity: isUpdating ? 0.6 : 1 }}>Mark as incomplete</button>
            ) : (
              <button type="button" disabled={isUpdating} onClick={() => updateBriefWorkflow(brief, "complete")} style={{ ...buttonStyle, background: "#dcfce7", color: "#166534", borderColor: "#86efac", opacity: isUpdating ? 0.6 : 1 }}>Mark complete</button>
            )}
            <button type="button" disabled={isUpdating} onClick={() => updateBriefWorkflow(brief, "archive")} style={{ ...buttonStyle, background: "#fff7ed", color: "#9a3412", borderColor: "#fed7aa", opacity: isUpdating ? 0.6 : 1 }}>Archive</button>
          </div>
        </div>
        <p style={{ color: "#334155", fontSize: 13 }}><strong>Action:</strong> {brief.recommended_action}</p>
        <p style={{ color: "#334155", fontSize: 13, marginBottom: 0 }}><strong>Why it matters:</strong> {brief.why_this_matters}</p>
      </div>
    );
  }

  if (isCheckingAdmin) {
    return <div style={{ padding: 32 }}>Checking admin access...</div>;
  }

  if (!hasAdminAccess) {
    return (
      <div style={{ padding: 32 }}>
        <h1>Admin access required</h1>
        <p>You do not have permission to access Agent Smith Action Briefs.</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", padding: "32px 20px", color: "#0f172a" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: "#4f46e5", textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Agent Smith v3
          </div>
          <h1 style={{ margin: "8px 0", fontSize: 34 }}>Operating Console</h1>
          <p style={{ margin: 0, color: "#475569", maxWidth: 860, lineHeight: 1.55 }}>
            Manage Action Brief workflows and outputs across AI Presence Builder, Competitive, and Local. This is the internal execution layer that turns Toren findings into draft-ready work and completed-opportunity proof.
          </p>
        </div>

        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
            <select
              value={selectedAccountId}
              onChange={(event) => setSelectedAccountId(event.target.value)}
              style={{ minWidth: 320, border: "1px solid #cbd5e1", borderRadius: 10, padding: "10px 12px", fontWeight: 700 }}
            >
              <option value="">Select account...</option>
              {accounts.map((account) => (
                <option key={account.account_id} value={account.account_id}>
                  {getAccountLabel(account)} · {account.status || "status unknown"}
                </option>
              ))}
            </select>

            <button
              type="button"
              onClick={() => refreshAccountData(selectedAccountId)}
              disabled={!selectedAccountId || isLoading}
              style={{ ...buttonStyle, background: "white", color: "#334155", borderColor: "#cbd5e1", cursor: selectedAccountId && !isLoading ? "pointer" : "not-allowed", opacity: selectedAccountId && !isLoading ? 1 : 0.6 }}
            >
              Refresh
            </button>

            <button
              type="button"
              onClick={handleGenerateActionBriefs}
              disabled={!selectedAccountId || isGenerating || recommendations.length === 0}
              style={{ ...buttonStyle, cursor: selectedAccountId && !isGenerating && recommendations.length > 0 ? "pointer" : "not-allowed", opacity: selectedAccountId && !isGenerating && recommendations.length > 0 ? 1 : 0.6 }}
            >
              {isGenerating ? "Generating..." : "Generate AI Presence Briefs"}
            </button>
          </div>

          <div style={{ marginTop: 10, color: "#64748b", fontSize: 12, lineHeight: 1.45 }}>
            Competitive and Local Action Briefs are surfaced here when created from dashboard opportunity workflows. AI Presence Builder briefs can also be generated from open Entity Builder recommendations.
          </div>

          {status ? (
            <div style={{ marginTop: 12, color: status.toLowerCase().includes("failed") ? "#991b1b" : "#166534", fontWeight: 800, fontSize: 13 }}>
              {status}
            </div>
          ) : null}
        </div>

        {selectedAccount ? (
          <>
            <div style={{ ...cardStyle, marginBottom: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>Completed Opportunity Validation</h2>
                  <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 13 }}>
                    Completed opportunities are counted from all Action Brief product areas for this account.
                  </p>
                </div>
                <div style={{ color: "#64748b", fontSize: 12, textAlign: "right" }}>
                  Current run<br />{completionMetrics.latestRunId || "—"}
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 10 }}>
                {[
                  ["This run", completionMetrics.currentRun],
                  ["Last 30 days", completionMetrics.last30],
                  [`${completionMetrics.currentYear} YTD`, completionMetrics.ytd],
                  [`${completionMetrics.lastYearLabel}`, completionMetrics.lastYear],
                  ["All time", completionMetrics.allTime],
                ].map(([label, value]) => (
                  <div key={label} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: 14, background: "#f8fafc" }}>
                    <div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>{label}</div>
                    <div style={{ fontSize: 30, fontWeight: 950, marginTop: 6, color: "#166534" }}>{value}</div>
                    <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>marked complete</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 14, marginBottom: 16 }}>
              <div style={cardStyle}>
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>Open recommendations</div>
                <div style={{ fontSize: 30, fontWeight: 950, marginTop: 6 }}>{recommendations.length}</div>
                <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>AI Presence Builder queue</div>
              </div>
              <div style={cardStyle}>
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>Open Action Briefs</div>
                <div style={{ fontSize: 30, fontWeight: 950, marginTop: 6 }}>{briefs.filter((brief) => !brief.marked_complete_at).length}</div>
                <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>all product areas loaded</div>
              </div>
              <div style={cardStyle}>
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>Completed Briefs Loaded</div>
                <div style={{ fontSize: 30, fontWeight: 950, marginTop: 6 }}>{briefs.filter((brief) => Boolean(brief.marked_complete_at)).length}</div>
                <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>current visible list</div>
              </div>
              <div style={cardStyle}>
                <div style={{ color: "#64748b", fontSize: 12, fontWeight: 900 }}>Latest completed run</div>
                <div style={{ fontSize: 13, fontWeight: 900, marginTop: 8 }}>{latestCompletedRun?.run_id || "—"}</div>
                <div style={{ color: "#64748b", fontSize: 12, marginTop: 4 }}>{formatDate(latestCompletedRun?.completed_at || latestCompletedRun?.created_at)}</div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 14, marginBottom: 16 }}>
              {productAreaCounts.map((area) => (
                <div key={area.value} style={{ ...cardStyle, borderColor: getProductAreaPillStyle(area.value).borderColor }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ color: "#0f172a", fontSize: 14, fontWeight: 950 }}>{area.label}</div>
                    <span style={{ border: `1px solid ${getProductAreaPillStyle(area.value).borderColor}`, borderRadius: 999, padding: "4px 8px", fontSize: 11, fontWeight: 900, ...getProductAreaPillStyle(area.value) }}>
                      {area.total} loaded
                    </span>
                  </div>
                  <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.45, margin: "8px 0 0" }}>{area.description}</p>
                  <div style={{ display: "flex", gap: 10, marginTop: 10, color: "#475569", fontSize: 12, fontWeight: 850 }}>
                    <span>{area.open} open</span>
                    <span>•</span>
                    <span>{area.complete} complete</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}

        {selectedAccount && actionEvents.length > 0 ? (
          <div style={{ ...cardStyle, marginBottom: 16 }}>
            <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>Recent Action Brief Activity</h2>
            <div style={{ display: "grid", gap: 8 }}>
              {actionEvents.slice(0, 6).map((event) => (
                <div key={event.id} style={{ display: "flex", justifyContent: "space-between", gap: 12, borderBottom: "1px solid #f1f5f9", paddingBottom: 8 }}>
                  <div>
                    <div style={{ fontWeight: 900, color: "#334155" }}>{formatEventType(event.event_type)}</div>
                    <div style={{ color: "#64748b", fontSize: 12 }}>
                      {event.event_metadata?.action_brief_title || event.event_metadata?.workflow_action || "Account-level activity"}
                      {event.event_metadata?.product_area ? ` · ${getProductAreaLabel(event.event_metadata.product_area)}` : ""}
                    </div>
                  </div>
                  <div style={{ color: "#64748b", fontSize: 12, whiteSpace: "nowrap" }}>{formatDate(event.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 16 }}>
          <div style={cardStyle}>
            <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>Open Entity Builder Recommendations</h2>
            <p style={{ margin: "0 0 12px", color: "#64748b", fontSize: 12, lineHeight: 1.45 }}>
              These feed AI Presence Builder Action Brief generation. Competitive and Local briefs are created from their dashboard opportunity workflows.
            </p>
            {recommendations.length === 0 ? (
              <p style={{ color: "#64748b" }}>No open recommendations loaded for this account.</p>
            ) : (
              <div style={{ display: "grid", gap: 10 }}>
                {recommendations.slice(0, 10).map((recommendation) => (
                  <div key={recommendation.id} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <strong>{recommendation.title}</strong>
                      <span style={{ color: recommendation.priority === "High" ? "#b91c1c" : "#475569", fontSize: 12, fontWeight: 900 }}>
                        {recommendation.priority}
                      </span>
                    </div>
                    <div style={{ color: "#64748b", fontSize: 12, marginTop: 6 }}>
                      {recommendation.recommendation_type} · {recommendation.entity_name || recommendation.canonical_entity_key || "entity"}
                    </div>
                    <p style={{ color: "#334155", fontSize: 13, marginBottom: 0 }}>{recommendation.summary}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={cardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start", marginBottom: 12, flexWrap: "wrap" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18 }}>Generated Action Briefs</h2>
                <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: 12 }}>
                  Showing {scopedBriefs.length} of {briefs.length} loaded briefs · active work is shown first.
                </p>
              </div>
              <select
                value={productAreaFilter}
                onChange={(event) => setProductAreaFilter(event.target.value)}
                style={{ border: "1px solid #cbd5e1", borderRadius: 999, padding: "8px 11px", color: "#334155", fontSize: 12, fontWeight: 900, background: "white" }}
              >
                <option value="all">All product areas</option>
                {AGENT_SMITH_PRODUCT_AREAS.map((area) => (
                  <option key={area.value} value={area.value}>{area.label}</option>
                ))}
              </select>
            </div>

            <div style={{ borderTop: "1px solid #f1f5f9", paddingTop: 12, marginBottom: 14 }}>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ color: "#475569", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Filter briefs
                </span>
                <span style={{ color: "#64748b", fontSize: 11, fontWeight: 900 }}>Status:</span>
                {[{ value: "all", label: "All" }, { value: "open", label: "Open" }, { value: "complete", label: "Complete" }].map((filter) => {
                  const active = actionBriefFilter === filter.value;
                  return <button key={filter.value} type="button" title={filter.value === "all" ? "Show all Action Briefs." : `${filter.label} completion status.`} aria-pressed={active} onClick={() => setActionBriefFilter(filter.value)} style={{ border: `1px solid ${active ? "#4f46e5" : "#cbd5e1"}`, background: active ? "#eef2ff" : "#f8fafc", color: active ? "#3730a3" : "#334155", borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 900, cursor: "pointer", boxShadow: active ? "0 0 0 2px rgba(79,70,229,0.12)" : "none" }}>{filter.label}</button>;
                })}
                <span style={{ color: "#64748b", fontSize: 11, fontWeight: 900, marginLeft: 4 }}>Action/work type:</span>
                {ACTION_BRIEF_FILTERS.map((filter) => {
                  const active = actionBriefFilter === filter.value;
                  const filterStyle = filter.style;
                  return <button key={filter.value} type="button" title={filter.description} aria-pressed={active} onClick={() => setActionBriefFilter(filter.value)} style={{ border: `1px solid ${active ? "#4f46e5" : filterStyle.borderColor}`, background: active ? "#eef2ff" : filterStyle.background, color: active ? "#3730a3" : filterStyle.color, borderRadius: 999, padding: "5px 9px", fontSize: 11, fontWeight: 900, cursor: "pointer", boxShadow: active ? "0 0 0 2px rgba(79,70,229,0.12)" : "none" }}>{filter.label}</button>;
                })}
                {actionBriefFilter !== "all" ? (
                  <button type="button" onClick={() => setActionBriefFilter("all")} style={{ border: 0, background: "transparent", color: "#4f46e5", fontSize: 11, fontWeight: 900, cursor: "pointer" }}>
                    Clear filter
                  </button>
                ) : null}
              </div>
              <p style={{ margin: "8px 0 0", color: "#64748b", fontSize: 11 }}>
                Priority groups work by urgency and leverage; Category uses product area; Action type groups the kind of work required. Hover a filter for its plain-language meaning.
              </p>
            </div>

            {briefs.length === 0 ? (
              <p style={{ color: "#64748b" }}>No Agent Smith Action Briefs generated yet.</p>
            ) : scopedBriefs.length === 0 ? (
              <p style={{ color: "#64748b" }}>No Action Briefs match the selected filters.</p>
            ) : (
              <div style={{ display: "grid", gap: 18 }}>
                {openBriefs.length > 0 ? (
                  <div>
                    <div style={{ borderBottom: "1px solid #cbd5e1", paddingBottom: 8, marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 950, color: "#0f172a" }}>Active Action Briefs ({openBriefs.length})</div>
                      <div style={{ color: "#64748b", fontSize: 11, marginTop: 3 }}>Outstanding work that still needs review or execution.</div>
                    </div>
                    <div style={{ display: "grid", gap: 12 }}>
                      {openBriefs.map((brief) => renderActionBriefCard(brief))}
                    </div>
                    {actionBriefFilter !== "all" && completedBriefs.length === 0 ? (
                      <p style={{ color: "#64748b", fontSize: 11, margin: "10px 0 0" }}>No completed briefs match this filter.</p>
                    ) : null}
                  </div>
                ) : null}
                {completedBriefs.length > 0 ? (
                  <details open={completedSectionOpen} onToggle={(event) => setCompletedSectionOpen(event.currentTarget.open)}>
                    <summary style={{ cursor: "pointer", listStyle: "none", borderBottom: "1px solid #cbd5e1", paddingBottom: 8, fontSize: 15, fontWeight: 950, color: "#166534" }}>
                      Completed Action Briefs ({completedBriefs.length})
                    </summary>
                    <p style={{ color: "#64748b", fontSize: 11, margin: "8px 0 10px" }}>Completed in the current loaded account/run; reopen to return it to active work.</p>
                    <div style={{ display: "grid", gap: 12, marginTop: 10 }}>
                      {completedBriefs.map((brief) => renderActionBriefCard(brief))}
                    </div>
                  </details>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AgentSmithActionBriefs;
